import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import Seo from "../../../shared/ui/Seo";
import AccountSidebar from "../../account/components/AccountSidebar";
import { getOrders, trackOrder } from "../api";
import { openInvoicePdf } from "../utils/invoice";
import { formatPrice } from "../../checkout/utils/pricing";
import { S3_URL_PRODUCTS } from "../../../shared/utils/constants";
import "../styles/my-orders.css";

const PAGE_SIZE = 5;
const ITEMS_PREVIEW = 3;

const OrderSkeleton = () => (
  <div className="order-card order-card--skeleton" aria-hidden="true">
    <div className="order-card__head">
      <span className="skeleton-line w-120" />
      <span className="skeleton-line w-80" />
    </div>
    <div className="order-card__items">
      {[0, 1].map((i) => (
        <div key={i} className="order-item">
          <span className="order-item__thumb skeleton-block" />
          <span className="skeleton-line w-200" />
        </div>
      ))}
    </div>
  </div>
);

const OrderCard = ({ order, invoiceLoading, onInvoice }) => {
  const items = order.basicProductResponseDTOList || [];
  const detailsLink = `/orderdetails/${order.orderDBid}`;

  return (
    <article className="order-card">
      <header className="order-card__head">
        <div className="order-card__id">
          <span className="order-card__label">Order</span>
          <Link to={detailsLink} className="order-card__number">
            #{order.orderTrackingNumber}
          </Link>
        </div>
        {order.orderStatus && <span className="order-status">{order.orderStatus}</span>}
      </header>

      <ul className="order-card__items">
        {items.slice(0, ITEMS_PREVIEW).map((product) => {
          const line = product.lineItemsDTOS?.[0];
          const image = product.productImages?.[0];
          return (
            <li key={product.productId} className="order-item">
              <span className="order-item__thumb">
                {image ? (
                  <img src={`${S3_URL_PRODUCTS}${image}`} alt="" loading="lazy" />
                ) : (
                  <i className="ph ph-package" />
                )}
              </span>
              <span className="order-item__name">{product.productName}</span>
              {line && (
                <span className="order-item__meta">
                  <span className="order-item__qty">Qty {line.lineItemQuantity}</span>
                  <span className="order-item__price">LKR {formatPrice(line.lineItemPrice)}</span>
                </span>
              )}
            </li>
          );
        })}
      </ul>
      {items.length > ITEMS_PREVIEW && (
        <Link to={detailsLink} className="order-card__more">
          +{items.length - ITEMS_PREVIEW} more {items.length - ITEMS_PREVIEW === 1 ? "item" : "items"}
        </Link>
      )}

      <footer className="order-card__foot">
        <span className="order-card__count">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
        <div className="order-card__actions">
          <button
            type="button"
            className="ui-btn ui-btn--ghost ui-btn--sm"
            onClick={() => onInvoice(order.orderTrackingNumber)}
            disabled={invoiceLoading}
          >
            <i className={`ph ${invoiceLoading ? "ph-spinner ui-btn__spin" : "ph-file-text"}`} />
            {invoiceLoading ? "Preparing…" : "Invoice"}
          </button>
          <Link to={detailsLink} className="ui-btn ui-btn--primary ui-btn--sm">
            View details
            <i className="ph ph-arrow-right" />
          </Link>
        </div>
      </footer>
    </article>
  );
};

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadingInvoiceId, setLoadingInvoiceId] = useState(null);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setHasError(false);
    getOrders(page, PAGE_SIZE)
      .then((response) => {
        if (ignore) return;
        setOrders(response.content || []);
        setPageCount(response.totalPages || 0);
      })
      .catch((error) => {
        console.error("Error fetching orders:", error);
        if (!ignore) setHasError(true);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [page, reloadKey]);

  const changePage = ({ selected }) => {
    setPage(selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInvoice = async (orderNumber) => {
    setLoadingInvoiceId(orderNumber);
    try {
      openInvoicePdf(await trackOrder(orderNumber));
    } catch (error) {
      console.error("Error fetching order details or generating invoice:", error);
    } finally {
      setLoadingInvoiceId(null);
    }
  };

  let content;
  if (isLoading) {
    content = [0, 1, 2].map((i) => <OrderSkeleton key={i} />);
  } else if (hasError) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-warning-circle ui-empty__icon" />
        <h2 className="ui-empty__title">We couldn't load your orders</h2>
        <p className="ui-empty__text">Please check your connection and try again.</p>
        <button type="button" className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
          Try again
        </button>
      </div>
    );
  } else if (orders.length === 0) {
    content = (
      <div className="ui-empty">
        <i className="ph ph-shopping-bag-open ui-empty__icon" />
        <h2 className="ui-empty__title">No orders yet</h2>
        <p className="ui-empty__text">When you place an order, it will show up here.</p>
        <Link to="/products" className="ui-btn ui-btn--primary ui-btn--sm">
          Start shopping
        </Link>
      </div>
    );
  } else {
    content = orders.map((order) => (
      <OrderCard
        key={order.orderDBid}
        order={order}
        invoiceLoading={loadingInvoiceId === order.orderTrackingNumber}
        onInvoice={handleInvoice}
      />
    ));
  }

  return (
    <section className="account-page py-20">
      <Seo title="My Orders" noindex />
      <div className="account-layout">
        <AccountSidebar />
        <div className="account-main">
          <div className="ui-head">
            <h1 className="ui-head__title">My Orders</h1>
            <p className="ui-head__text">Track your orders, view details and download invoices.</p>
          </div>

          <div className="orders-list">{content}</div>

          {!isLoading && !hasError && pageCount > 1 && (
            <ReactPaginate
              previousLabel={<i className="ph ph-caret-left" aria-label="Previous page" />}
              nextLabel={<i className="ph ph-caret-right" aria-label="Next page" />}
              breakLabel="…"
              pageCount={pageCount}
              marginPagesDisplayed={1}
              pageRangeDisplayed={3}
              onPageChange={changePage}
              forcePage={page}
              containerClassName="orders-pagination"
              pageLinkClassName="orders-pagination__link"
              previousLinkClassName="orders-pagination__link"
              nextLinkClassName="orders-pagination__link"
              breakLinkClassName="orders-pagination__link"
              activeLinkClassName="is-active"
              disabledLinkClassName="is-disabled"
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default MyOrders;
